import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import apiService from '../../services/apiService';

const TransferList = () => {
  const navigate = useNavigate();
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTransfers, setFilteredTransfers] = useState([]);
  
  // Filtros
  const [statusFilter, setStatusFilter] = useState('all');
  const [branchOriginFilter, setBranchOriginFilter] = useState('all');
  const [branchDestinyFilter, setBranchDestinyFilter] = useState('all');
  const [branches, setBranches] = useState([]);

  // Definir applyFilters con useCallback antes de useEffect
  const applyFilters = useCallback(() => {
    let filtered = [...transfers];
    
    // Filtrar por término de búsqueda
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        transfer =>
          transfer.client_origin?.first_name?.toLowerCase().includes(lowercasedSearch) ||
          transfer.client_origin?.father_last_name?.toLowerCase().includes(lowercasedSearch) ||
          transfer.client_destiny?.first_name?.toLowerCase().includes(lowercasedSearch) ||
          transfer.client_destiny?.father_last_name?.toLowerCase().includes(lowercasedSearch) ||
          transfer.id.toString().includes(searchTerm)
      );
    }
    
    // Filtrar por estado
    if (statusFilter !== 'all') {
      filtered = filtered.filter(transfer => transfer.status === statusFilter);
    }
    
    // Filtrar por sucursal de origen
    if (branchOriginFilter !== 'all') {
      filtered = filtered.filter(transfer => transfer.branch_origin.id === parseInt(branchOriginFilter));
    }
    
    // Filtrar por sucursal de destino
    if (branchDestinyFilter !== 'all') {
      filtered = filtered.filter(transfer => transfer.branch_destiny.id === parseInt(branchDestinyFilter));
    }
    
    setFilteredTransfers(filtered);
    setPage(0);
  }, [searchTerm, statusFilter, branchOriginFilter, branchDestinyFilter, transfers]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Cargar transferencias
        const transfersData = await apiService.getTransfers();
        setTransfers(transfersData);
        setFilteredTransfers(transfersData);
        
        // Cargar sucursales para filtros
        const branchesData = await apiService.getBranches();
        setBranches(branchesData);
      } catch (err) {
        console.error('Error al cargar transferencias:', err);
        setError('Error al cargar las transferencias. Por favor, intente nuevamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleCreateTransfer = () => {
    navigate('/transfers/create');
  };

  const handleViewTransfer = (id) => {
    navigate(`/transfers/view/${id}`);
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setBranchOriginFilter('all');
    setBranchDestinyFilter('all');
    setSearchTerm('');
  };

  const getStatusChip = (status) => {
    let color;
    let label;
    
    switch (status) {
      case 'pending':
        color = 'warning';
        label = 'Pendiente';
        break;
      case 'processed':
        color = 'info';
        label = 'Procesada';
        break;
      case 'canceled':
        color = 'error';
        label = 'Cancelada';
        break;
      case 'finished':
        color = 'success';
        label = 'Finalizada';
        break;
      default:
        color = 'default';
        label = status;
    }
    
    return <Chip label={label} color={color} size="small" />;
  };

  const formatCurrency = (amount, currency) => {
    const formatter = new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: currency === 'PEN' ? 'PEN' : 'USD',
      minimumFractionDigits: 2
    });
    
    return formatter.format(amount);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Transferencias</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateTransfer}
        >
          Nueva Transferencia
        </Button>
      </Box>

      <Paper sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              variant="outlined"
              placeholder="Buscar por cliente o ID..."
              value={searchTerm}
              onChange={handleSearchChange}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={clearSearch}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel id="status-filter-label">Estado</InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                label="Estado"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="pending">Pendiente</MenuItem>
                <MenuItem value="processed">Procesada</MenuItem>
                <MenuItem value="canceled">Cancelada</MenuItem>
                <MenuItem value="finished">Finalizada</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel id="branch-origin-filter-label">Sucursal Origen</InputLabel>
              <Select
                labelId="branch-origin-filter-label"
                value={branchOriginFilter}
                label="Sucursal Origen"
                onChange={(e) => setBranchOriginFilter(e.target.value)}
              >
                <MenuItem value="all">Todas</MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.branch_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel id="branch-destiny-filter-label">Sucursal Destino</InputLabel>
              <Select
                labelId="branch-destiny-filter-label"
                value={branchDestinyFilter}
                label="Sucursal Destino"
                onChange={(e) => setBranchDestinyFilter(e.target.value)}
              >
                <MenuItem value="all">Todas</MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.branch_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} display="flex" justifyContent="flex-end">
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={clearFilters}
            >
              Limpiar Filtros
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell>Origen</TableCell>
              <TableCell>Destino</TableCell>
              <TableCell>Cliente Origen</TableCell>
              <TableCell>Cliente Destino</TableCell>
              <TableCell>Portavalor</TableCell>
              <TableCell>Monto</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTransfers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  No se encontraron transferencias
                </TableCell>
              </TableRow>
            ) : (
              filteredTransfers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((transfer) => (
                  <TableRow key={transfer.id}>
                    <TableCell>{transfer.id}</TableCell>
                    <TableCell>{new Date(transfer.register_date).toLocaleString()}</TableCell>
                    <TableCell>{transfer.branch_origin?.branch_name || 'N/A'}</TableCell>
                    <TableCell>{transfer.branch_destiny?.branch_name || 'N/A'}</TableCell>
                    <TableCell>
                      {`${transfer.client_origin?.first_name || ''} ${transfer.client_origin?.father_last_name || ''}`}
                    </TableCell>
                    <TableCell>
                      {`${transfer.client_destiny?.first_name || ''} ${transfer.client_destiny?.father_last_name || ''}`}
                    </TableCell>
                    <TableCell>
                      {transfer.user ? `${transfer.user.first_name || ''} ${transfer.user.father_last_name || ''}` : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {formatCurrency(transfer.amount, transfer.currency_type)}
                    </TableCell>
                    <TableCell>
                      {getStatusChip(transfer.status)}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver detalles">
                        <IconButton
                          color="info"
                          onClick={() => handleViewTransfer(transfer.id)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredTransfers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </TableContainer>
    </Box>
  );
};

export default TransferList; 